/**
 * OperatorPortalApi — the OPERATOR half of the portal's HTTP calls.
 *
 * Built on the portal's own client (`portalClient` + `payloadOf` from
 * PortalApi.js): the portal token, silent refresh and the "never send the
 * admin token" guarantee all come from there. Nothing here sends an
 * operator id — the API takes the operator from the signed-in principal
 * and ignores one in the request.
 *
 * Response shapes (see ph-core portalOperatorViews.helper.ts):
 *
 *   GET  /portal/operator/assignments?from&to
 *        → { from, to, currency?, currencies, total, trips: OperatorTrip[] }
 *        `currency` is there ONLY when every record in scope settles in the
 *        same one — a login answering for a currency-split pair has no
 *        single settlement currency, so the field is absent rather than
 *        guessed. `currencies` is always there. Every figure on a trip
 *        carries its own `money.currency`, which is what the screens read.
 *   OperatorTrip = {
 *     _id, booking_reference, status, booking_type, date_datetime,
 *     from: { address }, to: { address }, category,
 *     passengers: { adults, children?, booster_seat?, convertible_seat?, infant_carrier? },
 *     customer_name, customer_phone?, flight_number?, operator_comments?,
 *     programme?, event_name?, role?, driver?: { id, full_name? },
 *     vehicle?: { id, type?, plate_number?, color? }, needs_driver, acknowledged,
 *     driver_status?: { state }, overtime_total_minutes,
 *     money: { currency, fee, cash_to_collect?: { amount, currency },
 *              cash_in_settlement, balance, price?: { amount, currency },
 *              adjustments?: { amount, currency } } }
 *   POST /portal/operator/assign       { booking_id, driver_id?, car_id?, role? } → OperatorTrip
 *   POST /portal/operator/acknowledge  { booking_id }                           → OperatorTrip
 *   GET  /portal/operator/statement?from&to
 *        → { from, to, currency, trip_count, fees, cash_collected, paid, outstanding,
 *            lines: [{ _id, booking_reference, status, date_datetime, customer_name,
 *                      from, to, category, pax, fee, cash_collected, balance }],
 *            by_operator?: [{ operator_id, currency, trip_count, fees,
 *                             cash_collected, paid, outstanding, lines }] }
 *        The scalars are the LOGIN'S OWN record's. `by_operator` is sent only
 *        when the login answers for more than one record, and is each
 *        record's own statement in its own settlement currency. Nothing is
 *        summed or converted across the entries — see `statementBuckets`.
 *   GET/POST/DELETE /portal/operator/drivers   → [{ Id, full_name, phone_number, active }]
 *   GET/POST/DELETE /portal/operator/vehicles  → [{ Id, type, color, plate_number, active }]
 *   GET    /portal/operator/portal-users  → [{ cognito_sub, driver_id, driver_name, email,
 *                                             active, enrolment_pending, createdAt, created_by }]
 *   POST   /portal/operator/portal-users  { action: 'grant', driver_id, email? }
 *                                       | { action: 'regenerate', driver_id }
 *        → { action, portal_user, enrolment: { cognito_sub, email, enrolment_token, expires_at } }
 *   DELETE /portal/operator/portal-users?cognito_sub=  → the revoked login
 */
import { payloadOf, portalClient } from './PortalApi';
import compact from '../Utils/compactParams';

/**
 * `currency` is passed through EXACTLY as the API sent it, absent included.
 * A login answering for a currency-split pair settles in two, so there is no
 * single one to report and none is invented here: a screen that needs a
 * currency reads the trip's own `money.currency`. `currencies` is normalised
 * to an array so a caller never has to test for it, and falls back to the
 * single `currency` only when the API sent that.
 */
export const fetchOperatorAssignments = async ({ from, to } = {}) => {
  const payload = payloadOf(await portalClient.get('/portal/operator/assignments', {
    params: compact({ from, to }),
  }));
  const currencies = Array.isArray(payload?.currencies) ? payload.currencies : [];

  return {
    ...payload,
    currencies: currencies.length || !payload?.currency ? currencies : [payload.currency],
    trips: Array.isArray(payload?.trips) ? payload.trips : [],
  };
};

/**
 * Puts one of the operator's own drivers and/or cars on a trip. An omitted
 * driver or car keeps its current value on the server.
 */
export const assignOperatorTrip = async ({
  bookingId, driverId, carId, role,
}) => payloadOf(await portalClient.post('/portal/operator/assign', compact({
  booking_id: bookingId,
  driver_id: driverId,
  car_id: carId,
  role,
})));

export const acknowledgeOperatorTrip = async (bookingId) => payloadOf(
  await portalClient.post('/portal/operator/acknowledge', { booking_id: bookingId }),
);

export const fetchOperatorStatement = async ({ from, to } = {}) => {
  const payload = payloadOf(await portalClient.get('/portal/operator/statement', {
    params: compact({ from, to }),
  }));
  return {
    ...payload,
    lines: Array.isArray(payload?.lines) ? payload.lines : [],
  };
};

const asList = (payload) => (Array.isArray(payload) ? payload : []);

/**
 * A `{ list, save, remove }` service for one of the operator's resources —
 * the shape DriverManagementSection / FleetManagementSection accept as their
 * `service` prop. `save` drops any `operator_id` the form adds: the API
 * would ignore it anyway, and not sending it keeps the contract honest.
 */
const resourceService = (path) => ({
  list: async () => asList(payloadOf(await portalClient.get(path))),
  save: async (record) => {
    // eslint-disable-next-line camelcase, no-unused-vars
    const { operator_id, ...body } = record || {};
    return payloadOf(await portalClient.post(path, body));
  },
  remove: async (id) => payloadOf(await portalClient.delete(path, { params: { id } })),
});

export const operatorDriverService = resourceService('/portal/operator/drivers');
export const operatorVehicleService = resourceService('/portal/operator/vehicles');

/* --- portal access for the operator's own drivers -------------------------
 * The operator invites its drivers to the portal, re-issues their link and
 * takes access away — the admin screen's grant / regenerate / revoke, scoped
 * by the API to this operator's own active drivers. Only a driver id (and an
 * address, the first time) is sent; never an operator id or a login kind.
 *
 * `grant` / `regenerate` resolve to `{ token, expiresAt, cognitoSub, login }`.
 * The token is the single-use enrolment link's secret: hand it straight to
 * the hand-over and let it die with it — never store it, never log it.
 */
const PORTAL_USERS_PATH = '/portal/operator/portal-users';

const toHandover = (payload) => ({
  token: payload?.enrolment?.enrolment_token,
  expiresAt: payload?.enrolment?.expires_at,
  cognitoSub: payload?.enrolment?.cognito_sub,
  login: payload?.portal_user,
});

export const operatorPortalUserService = {
  list: async () => asList(payloadOf(await portalClient.get(PORTAL_USERS_PATH))),
  // `email` may be left out to re-invite a driver on the address of their
  // previous (revoked) login.
  grant: async ({ driverId, email }) => toHandover(payloadOf(await portalClient.post(
    PORTAL_USERS_PATH,
    compact({ action: 'grant', driver_id: driverId, email }),
  ))),
  regenerate: async (driverId) => toHandover(payloadOf(await portalClient.post(
    PORTAL_USERS_PATH,
    { action: 'regenerate', driver_id: driverId },
  ))),
  revoke: async (cognitoSub) => payloadOf(await portalClient.delete(PORTAL_USERS_PATH, {
    params: { cognito_sub: cognitoSub },
  })),
};
